// SPDX-License-Identifier: GPL-3.0-or-later

// Считает уровень звука из MediaStream через AnalyserNode. 0..1 после нормализации.
// Держит по одному AudioContext/AnalyserNode на поток, чистит всё при release/releaseAll.

export type Unsubscribe = () => void;
export type LevelListener = (level: number) => void;

const SAMPLING_MS = 120;
const NORMALIZATION = 80; // Подобрано эмпирически: avg 80/255 уже похоже на разговор.

interface Entry {
  ctx: AudioContext;
  analyzer: AnalyserNode;
  // Uint8Array<ArrayBuffer>, чтобы DOM-метод getByteFrequencyData принял буфер в свежих TS-типах.
  data: Uint8Array<ArrayBuffer>;
  listeners: Set<LevelListener>;
}

export interface AudioLevelService {
  attach(id: string, stream: MediaStream, listener: LevelListener): Unsubscribe;
  release(id: string): void;
  releaseAll(): void;
}

export class WebAudioLevelService implements AudioLevelService {
  private entries = new Map<string, Entry>();
  private timer: ReturnType<typeof setInterval> | null = null;

  attach(id: string, stream: MediaStream, listener: LevelListener): Unsubscribe {
    let entry = this.entries.get(id);
    if (!entry) {
      const AnyAudioContext =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AnyAudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyzer = ctx.createAnalyser();
      analyzer.fftSize = 256;
      analyzer.smoothingTimeConstant = 0.5;
      source.connect(analyzer);
      entry = {
        ctx,
        analyzer,
        data: new Uint8Array(new ArrayBuffer(analyzer.frequencyBinCount)),
        listeners: new Set(),
      };
      this.entries.set(id, entry);
    }
    entry.listeners.add(listener);
    this.ensureTimer();
    return () => {
      entry?.listeners.delete(listener);
      if (entry && entry.listeners.size === 0) this.release(id);
    };
  }

  release(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    try {
      void entry.ctx.close();
    } catch {
      /* уже закрыт — игнорируем */
    }
    this.entries.delete(id);
    if (this.entries.size === 0 && this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  releaseAll(): void {
    for (const id of [...this.entries.keys()]) this.release(id);
  }

  private ensureTimer(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), SAMPLING_MS);
  }

  private tick(): void {
    for (const entry of this.entries.values()) {
      entry.analyzer.getByteFrequencyData(entry.data);
      let sum = 0;
      for (let i = 0; i < entry.data.length; i++) sum += entry.data[i] ?? 0;
      const avg = sum / entry.data.length;
      const level = Math.min(1, avg / NORMALIZATION);
      for (const l of entry.listeners) l(level);
    }
  }
}
